<style>
.menu-item {
  padding: 0;
  color: var(--text-color) !important;
}
.menu-item:hover {
  color: var(--hover-color) !important;
}

.sidebar {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.sidebar-expanded {
  width: 224px;
  overflow: hidden;
  padding: 3rem 1rem;
}

.sidebar-collapsed {
  width: 80px;
  overflow: hidden;
  padding: 3rem 1rem;
}

.menu-text {
  transition: all 0.3s ease;
  white-space: nowrap;
}

.menu-text-hidden {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.menu-icon {
  min-width: 2rem;
  width: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
}

.menu-link {
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin: 0.25rem 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  text-align: center;
}

.menu-link:hover {
  color: var(--hover-color) !important;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 0.2s ease;
}

.menu-link.active {
  color: var(--hover-color) !important;
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 0.2s ease;
}

.toggle-button {
  position: absolute;
  right: 1rem;
  top: 1rem;
  width: 2rem;
  height: 2rem;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  z-index: 10;
  transition: all 0.3s ease;
}

@media (max-width: 425px) {
  .sidebar {
    width: 224px !important;
  }
  .toggle-button {
    display: none;
  }
  .menu-link.active {
    background-color: #0d9488 !important;
  }
}

@media (max-width: 768px) {
  .menu-text {
    opacity: 1 !important;
    width: auto !important;
  }
}

.toggle-button:hover {
  background: #f8f8f8;
  transform: scale(1.1);
}

.toggle-button i {
  color: #333;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}
</style>

<template>
  <div :class="[isExpanded ? 'sidebar-expanded' : 'sidebar-collapsed', 'sidebar']">
    <!-- Toggle Button -->
    <div 
      @click="toggleSidebar"
      class="toggle-button text-right p-2"
    >
      <i :class="['fas', 'fa-bars']"></i>
    </div>

    <!-- Menu Items -->
    <div class="px-2 py-4">
      <template v-for="item in menuItems" :key="item.id">
        <div class="relative">
          <Link 
            v-if="canUserAccess(item.roles)"
            class="menu-link" 
            :class="{ 
              'active': isUrl(item.route.substring(1)),
              'opacity-50': isPremiumFeature(item.id) && isFreePlan
            }"
            :href="isPremiumFeature(item.id) && isFreePlan ? '/subscription' : item.route"
            @mouseenter="hoveredItem = item.id"
            @mouseleave="hoveredItem = null"
            @click="handleMenuItemClick"
          >
            <div class="menu-icon">
              <i 
                :class="[
                  'fas',
                  item.icon,
                  'mr-2',
                  isUrl(item.route.substring(1)) ? 'text-white' : 'text-gray-400 group-hover:text-white menu-item'
                ]" 
                :style="{'--text-color': account.textSidebarColor ?? 'black', '--hover-color': account.textHoverSidebarColor}"
              />
            </div>

            <div 
              :class="[
                'menu-text ml-3',
                {'menu-text-hidden': !isExpanded},
                isUrl(item.route.substring(1)) ? 'menu-item font-bold' : 'text-gray-400 group-hover:text-white menu-item'
              ]" 
              :style="{'--text-color': account.textSidebarColor ?? 'black', '--hover-color': account.textHoverSidebarColor}"
            >
              {{ item.name }}
            </div>

            <div v-if="isPremiumFeature(item.id) && isFreePlan" 
                 class="absolute right-0 top-1/2 transform -translate-y-1/2">
              <span class="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                PRO
              </span>
            </div>
          </Link>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'

export default {
  components: {
    Icon,
    Link,
  },
  props: {
    account: Object
  },
  created() {
    // En móviles siempre expandido, en desktop contraído
    const isMobile = window.innerWidth <= 425
    localStorage.setItem('sidebarExpanded', isMobile ? 'true' : 'false')
  },
  data() {
    return {
      role: this.$parent.auth.user.role,
      isExpanded: localStorage.getItem('sidebarExpanded') === 'true',
      hoveredItem: null,
      menuItems: [
        { id: 'dashboard', name: 'Escritorio', icon: 'fa-home', route: '/dashboard', roles: ['*'] },
        { id: 'clients', name: 'Clientes', icon: 'fa-address-book', route: '/clients', roles: ['admin', 'sales', 'financial'] },
        { id: 'products', name: 'Productos', icon: 'fa-boxes', route: '/products', roles: ['admin', 'endline', 'storer'] },
        { id: 'inventory', name: 'Inventario', icon: 'fa-warehouse', route: '/inventories', roles: ['admin', 'endline', 'storer'] },
        { id: 'orders', name: 'Ventas', icon: 'fa-chart-line', route: '/orders', roles: ['sales', 'admin', 'storer', 'financial'] },
        { id: 'shopping', name: 'Compras', icon: 'fa-truck', route: '/shoppings', roles: ['admin', 'financial'] },
        { id: 'providers', name: 'Proveedores', icon: 'fa-users', route: '/providers', roles: ['admin', 'financial'] },
        { id: 'spents', name: 'Gastos', icon: 'fa-file-invoice-dollar', route: '/spents', roles: ['admin', 'financial'] },
        { id: 'reports', name: 'Reportes', icon: 'fa-file', route: '/reports', roles: ['sales', 'admin'] },
        { id: 'backup', name: 'Respaldos', icon: 'fa-database', route: '/backup', roles: ['admin'] }
      ],
      freePlanFeatures: ['dashboard', 'clients', 'products', 'inventory', 'orders', 'reports']
    }
  },
  computed: {
    isFreePlan() {
      return this.account.plan === 'free'
    }
  },
  methods: {
    back() {
      this.$inertia.visit('/')
    },
    isUrl(...urls) {
      let currentUrl = this.$page.url.substr(1)
      if (urls[0] === '') {
        return currentUrl === ''
      }
      return urls.filter((url) => currentUrl.startsWith(url)).length
    },
    toggleSidebar() {
      this.isExpanded = !this.isExpanded
      localStorage.setItem('sidebarExpanded', this.isExpanded)
    },
    canUserAccess(roles) {
      return roles.includes('*') || roles.includes(this.role)
    },
    isPremiumFeature(featureId) {
      return !this.freePlanFeatures.includes(featureId)
    },
    handleMenuItemClick() {
      if (window.innerWidth <= 425) {
        this.isExpanded = false
        localStorage.setItem('sidebarExpanded', 'false')
      }
    }
  },
  mounted() {
    const savedState = localStorage.getItem('sidebarExpanded')
    if (savedState !== null) {
      this.isExpanded = savedState === 'true'
    }
  }
}
</script>
